import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X } from 'lucide-react';
import LanguageSelector from './LanguageSelector';

const Navbar: React.FC = () => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    // Reset scroll state when route changes
    setScrolled(false);
    window.scrollTo(0, 0);
    
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    
    // Check initial scroll position
    handleScroll();
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [location.pathname]);

  const navItems = [
    { path: '/', label: t('nav.home') },
    { path: '/about', label: t('nav.about') },
    { path: '/menu', label: t('nav.menu') },
    { path: '/gallery', label: t('nav.gallery') },
    { path: '/location', label: t('nav.location') },
  ];

  return (
    <nav className={`fixed w-full z-50 transition-all duration-500 ${
      scrolled 
        ? 'bg-luxury-cream/95 backdrop-blur-md shadow-xl py-4 border-b border-luxury-gold/20' 
        : 'bg-luxury-black/80 backdrop-blur-sm py-6 border-b border-luxury-gold/10'
    }`}>
      <div className="section-padding">
        <div className="flex justify-between items-center">
          <Link to="/" className="flex items-center group">
            <motion.div 
              key={location.pathname}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              className="relative"
            >
              <h1 className={`text-3xl font-display tracking-wider transition-colors duration-300 ${
                scrolled ? 'text-luxury-black' : 'text-luxury-cream'
              }`}>
                PACIFIC
                <span className="text-luxury-gold ml-2">78</span>
              </h1>
              <div className="absolute -bottom-1 left-0 w-0 h-0.5 bg-luxury-gold transition-all duration-300 group-hover:w-full" />
            </motion.div>
          </Link>

          <div className="hidden lg:flex items-center gap-10">
            {navItems.map((item, index) => (
              <motion.div
                key={item.path}
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <Link
                  to={item.path}
                  className={`relative font-body text-lg tracking-wide transition-colors duration-300 ${
                    scrolled ? 'text-luxury-black hover:text-luxury-gold' : 'text-luxury-cream hover:text-luxury-gold'
                  } ${location.pathname === item.path ? 'text-luxury-gold' : ''}`}
                >
                  <span className="relative">
                    {item.label}
                    <span className={`absolute -bottom-2 left-0 w-full h-0.5 bg-luxury-gold transform transition-transform duration-300 ${
                      location.pathname === item.path ? 'scale-x-100' : 'scale-x-0'
                    } group-hover:scale-x-100`} />
                  </span>
                </Link>
              </motion.div>
            ))}
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="ml-8"
            >
              <LanguageSelector scrolled={scrolled} />
            </motion.div>
          </div>

          <button
            onClick={() => setIsOpen(!isOpen)}
            className={`lg:hidden text-2xl transition-colors duration-300 ${
              scrolled ? 'text-luxury-black' : 'text-luxury-cream'
            }`}
          >
            {isOpen ? <X size={28} /> : <Menu size={28} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-luxury-cream shadow-2xl border-t border-luxury-gold/20"
          >
            <div className="section-padding py-6">
              {navItems.map((item, index) => (
                <motion.div
                  key={item.path}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.05 }}
                >
                  <Link
                    to={item.path}
                    onClick={() => setIsOpen(false)}
                    className={`block py-4 text-lg font-body tracking-wide text-luxury-black hover:text-luxury-gold transition-colors ${
                      location.pathname === item.path ? 'text-luxury-gold' : ''
                    }`}
                  >
                    {item.label}
                  </Link>
                </motion.div>
              ))}
              <motion.div 
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.25 }}
                className="pt-4 border-t border-luxury-gold/20"
              >
                <LanguageSelector scrolled={true} />
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

export default Navbar;